import { useQuery } from "@tanstack/react-query";
import { PhoneIncoming, PhoneMissed, PhoneOutgoing, Star } from "lucide-react";
import { Link } from "react-router";
import { get } from "../../api/client";
import type { CallListItem, Contact, Page } from "../../api/types";
import { useCan, useSession } from "../../auth/session";
import { Button } from "../../components/ui/Button";
import { Card, EmptyState, ErrorState, Loading, PageHeader } from "../../components/ui/misc";
import { formatDateTime, formatPhone } from "../../lib/format";
import { useMediaQuery } from "../../lib/useMediaQuery";
import { useDialer } from "../../voice/dialer";
import { Softphone } from "./Softphone";

/** Landing page: favorites and recent calls for quick dialing. On small screens it also hosts the softphone. */
export function HomePage() {
  const { data: session } = useSession();
  const { can, canAny } = useCan();
  const wide = useMediaQuery("(min-width: 1024px)");
  const dial = useDialer((s) => s.dial);
  const canDial = can("calls.initiate");
  const favorites = useQuery({
    queryKey: ["contacts", { favoritesOnly: true }],
    queryFn: () => get<Page<Contact>>("/contacts?favoritesOnly=true&pageSize=12"),
    enabled: can("contacts.read"),
  });
  const recent = useQuery({
    queryKey: ["calls", "recent"],
    queryFn: () => get<Page<CallListItem>>("/calls?pageSize=8"),
    enabled: canAny("calls.read.own", "calls.read.team", "calls.read.organization"),
  });

  return (
    <div className="mx-auto max-w-4xl">
      <PageHeader title={`Hello, ${session?.user.name.split(" ")[0] ?? ""}`} description={session?.organization?.name} />
      {!wide && canAny("calls.initiate", "calls.receive") && (
        <section aria-label="Softphone" className="surface mb-5 rounded-xl border border-default"><Softphone /></section>
      )}
      <div className="grid gap-5 md:grid-cols-2">
        {can("contacts.read") && (
          <Card title="Favorites" actions={<Link className="text-sm text-accent-600 hover:underline" to="/contacts">All contacts</Link>}>
            {favorites.isLoading ? <Loading /> : favorites.error ? <ErrorState error={favorites.error} onRetry={() => void favorites.refetch()} /> :
              favorites.data!.items.length === 0 ? <EmptyState title="No favorites yet" description="Star contacts to dial them from here." /> : (
                <ul className="divide-y divide-[var(--border)]">
                  {favorites.data!.items.map((c) => (
                    <li key={c.id} className="flex items-center justify-between gap-2 py-2">
                      <div className="min-w-0">
                        <p className="flex items-center gap-1 truncate text-sm font-medium text-default"><Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" aria-hidden="true" />{c.name}</p>
                        <p className="text-xs text-subtle">{formatPhone(c.phone)}</p>
                      </div>
                      <Button size="sm" onClick={() => dial(c.phone)} disabled={!canDial} aria-label={`Call ${c.name}`}>Call</Button>
                    </li>
                  ))}
                </ul>
              )}
          </Card>
        )}
        {canAny("calls.read.own", "calls.read.team", "calls.read.organization") && (
          <Card title="Recent calls" actions={<Link className="text-sm text-accent-600 hover:underline" to="/history">History</Link>}>
            {recent.isLoading ? <Loading /> : recent.error ? <ErrorState error={recent.error} onRetry={() => void recent.refetch()} /> :
              recent.data!.items.length === 0 ? <EmptyState title="No calls yet" /> : (
                <ul className="divide-y divide-[var(--border)]">
                  {recent.data!.items.map((c) => {
                    const Icon = c.missed ? PhoneMissed : c.direction === "INBOUND" ? PhoneIncoming : PhoneOutgoing;
                    return (
                      <li key={c.id} className="flex items-center justify-between gap-2 py-2">
                        <Link to={`/history/${c.id}`} className="focus-ring flex min-w-0 items-center gap-2 rounded">
                          <Icon className={`h-4 w-4 shrink-0 ${c.missed ? "text-red-600" : "text-subtle"}`} aria-label={c.missed ? "Missed" : c.direction === "INBOUND" ? "Inbound" : "Outbound"} />
                          <span className="min-w-0">
                            <span className="block truncate text-sm font-medium text-default">{c.contact?.name ?? formatPhone(c.externalNumber)}</span>
                            <span className="block text-xs text-subtle">{formatDateTime(c.createdAt)}</span>
                          </span>
                        </Link>
                        {c.externalNumber !== "anonymous" && (
                          <Button size="sm" onClick={() => dial(c.externalNumber)} disabled={!canDial} aria-label={`Call back ${c.contact?.name ?? formatPhone(c.externalNumber)}`}>Call</Button>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
          </Card>
        )}
      </div>
    </div>
  );
}
